import { type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import type { CreateOutboundRequest } from '../../../types/case-container'

interface CreateCommunicationModalProps {
  onClose: () => void
  onCreate: (data: CreateOutboundRequest) => void
  creating: boolean
}

const INPUT_CLASS =
  'w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm'
const LABEL_CLASS = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1'

export function CreateCommunicationModal({
  onClose,
  onCreate,
  creating,
}: CreateCommunicationModalProps): ReactElement {
  const { t } = useTranslation('cases')
  const [formData, setFormData] = useState<CreateOutboundRequest>({
    communicationType: 'email',
    senderName: '',
    recipientNames: [''],
    subject: '',
    body: '',
  })

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (formData.subject && formData.body && formData.senderName && formData.recipientNames[0]) {
      onCreate(formData)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('communications.newOutbound')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TopRow formData={formData} setFormData={setFormData} />
          <RecipientField formData={formData} setFormData={setFormData} />
          <SubjectField formData={formData} setFormData={setFormData} />
          <BodyField formData={formData} setFormData={setFormData} />
          <ActionsRow onClose={onClose} creating={creating} />
        </form>
      </div>
    </div>
  )
}

interface FieldProps {
  formData: CreateOutboundRequest
  setFormData: (next: CreateOutboundRequest) => void
}

function TopRow({ formData, setFormData }: FieldProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className={LABEL_CLASS}>{t('communications.typeLabel')}</label>
        <select
          value={formData.communicationType}
          onChange={(event) => {
            setFormData({ ...formData, communicationType: event.target.value })
          }}
          className={INPUT_CLASS}
        >
          <option value="email">{t('communications.typeEmail')}</option>
          <option value="letter">{t('communications.typeLetter')}</option>
          <option value="phone">{t('communications.typePhone')}</option>
          <option value="meeting">{t('communications.typeMeeting')}</option>
        </select>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('communications.senderName')}</label>
        <input
          type="text"
          value={formData.senderName}
          onChange={(event) => {
            setFormData({ ...formData, senderName: event.target.value })
          }}
          className={INPUT_CLASS}
          required
        />
      </div>
    </div>
  )
}

function RecipientField({ formData, setFormData }: FieldProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className={LABEL_CLASS}>{t('communications.recipient')}</label>
      <input
        type="text"
        value={formData.recipientNames[0]}
        onChange={(event) => {
          setFormData({ ...formData, recipientNames: [event.target.value] })
        }}
        className={INPUT_CLASS}
        required
      />
    </div>
  )
}

function SubjectField({ formData, setFormData }: FieldProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className={LABEL_CLASS}>{t('communications.subject')}</label>
      <input
        type="text"
        value={formData.subject}
        onChange={(event) => {
          setFormData({ ...formData, subject: event.target.value })
        }}
        className={INPUT_CLASS}
        required
      />
    </div>
  )
}

function BodyField({ formData, setFormData }: FieldProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className={LABEL_CLASS}>{t('communications.body')}</label>
      <textarea
        value={formData.body}
        onChange={(event) => {
          setFormData({ ...formData, body: event.target.value })
        }}
        className={INPUT_CLASS}
        rows={5}
        required
      />
    </div>
  )
}

function ActionsRow({
  onClose,
  creating,
}: {
  onClose: () => void
  creating: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={creating}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {creating && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('communications.send')}
      </button>
    </div>
  )
}
